/**
 * Tema Corporativo Principal
 * Configuración completa del tema Material UI con identidad corporativa
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import { colors, typography, shape, shadows, gradients, logoPaths } from './designTokens';

type PaletteMode = 'light' | 'dark';

// ============================================
// CONFIGURACIÓN DE TIPOGRAFÍA
// ============================================
const typographyConfig: ThemeOptions['typography'] = {
  fontFamily: typography.fontFamily,
  h1: {
    fontFamily: typography.fontFamilyDisplay,
    fontSize: '2.4rem',
    fontWeight: typography.fontWeight.bold,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    color: colors.text.primary,
  },
  h2: {
    fontFamily: typography.fontFamilyDisplay,
    fontSize: '2rem',
    fontWeight: typography.fontWeight.bold,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    color: colors.text.primary,
  },
  h3: {
    fontFamily: typography.fontFamilyDisplay,
    fontSize: '1.6rem',
    fontWeight: typography.fontWeight.semibold,
    lineHeight: 1.4,
    letterSpacing: '-0.01em',
    color: colors.text.primary,
  },
  h4: {
    fontFamily: typography.fontFamilyDisplay,
    fontSize: '1.4rem',
    fontWeight: typography.fontWeight.semibold,
    lineHeight: 1.4,
    letterSpacing: '-0.005em',
    color: colors.text.primary,
  },
  h5: {
    fontFamily: typography.fontFamilyDisplay,
    fontSize: '1.2rem',
    fontWeight: typography.fontWeight.semibold,
    lineHeight: 1.5,
    color: colors.text.primary,
  },
  h6: {
    fontFamily: typography.fontFamily,
    fontSize: '1rem',
    fontWeight: typography.fontWeight.medium,
    lineHeight: 1.5,
    color: colors.text.primary,
  },
  body1: {
    fontFamily: typography.fontFamily,
    fontSize: '0.8rem',
    fontWeight: typography.fontWeight.regular,
    lineHeight: 1.6,
    color: colors.text.primary,
  },
  body2: {
    fontFamily: typography.fontFamily,
    fontSize: '0.7rem',
    fontWeight: typography.fontWeight.regular,
    lineHeight: 1.6,
    color: colors.text.secondary,
  },
  button: {
    fontFamily: typography.fontFamily,
    textTransform: 'none',
    fontWeight: typography.fontWeight.medium,
    letterSpacing: '0.02em',
  },
  caption: {
    fontFamily: typography.fontFamily,
    fontSize: '0.6rem',
    fontWeight: typography.fontWeight.regular,
    color: colors.text.secondary,
  },
  overline: {
    fontFamily: typography.fontFamily,
    fontSize: '0.6rem',
    fontWeight: typography.fontWeight.medium,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: colors.text.secondary,
  },
  subtitle1: {
    fontFamily: typography.fontFamily,
    fontSize: '0.8rem',
    fontWeight: typography.fontWeight.medium,
    lineHeight: 1.5,
    color: colors.text.primary,
  },
  subtitle2: {
    fontFamily: typography.fontFamily,
    fontSize: '0.7rem',
    fontWeight: typography.fontWeight.medium,
    lineHeight: 1.5,
    color: colors.text.secondary,
  },
};

// ============================================
// CONFIGURACIÓN DE COMPONENTES
// ============================================
const createComponents = (mode: PaletteMode): ThemeOptions['components'] => {
  const isDark = mode === 'dark';
  const surfaceColor = isDark ? '#1E293B' : colors.background.paper;
  const inputBorderColor = isDark ? 'rgba(255, 255, 255, 0.24)' : colors.neutral.main;
  const dividerColor = isDark ? 'rgba(255, 255, 255, 0.12)' : colors.neutral.main;
  const textSecondaryColor = isDark ? '#94A3B8' : colors.text.secondary;
  const textPrimaryColor = isDark ? '#F8FAFC' : colors.text.primary;

  return {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadius.medium,
          padding: '8px 19.2px',
          fontSize: '0.75rem',
          fontWeight: typography.fontWeight.medium,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: shadows[2],
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&:disabled': {
            opacity: 0.5,
          },
        },
        contained: {
          '&:hover': {
            boxShadow: shadows[4],
          },
        },
        outlined: {
          borderWidth: shape.borderWidth.medium,
          '&:hover': {
            borderWidth: shape.borderWidth.medium,
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(11, 61, 145, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadius.large,
          boxShadow: shadows[2],
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: `1px solid ${dividerColor}`,
          backgroundColor: surfaceColor,
          '&:hover': {
            boxShadow: shadows[4],
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadius.medium,
          backgroundImage: 'none',
          backgroundColor: surfaceColor,
        },
        elevation1: {
          boxShadow: shadows[1],
        },
        elevation2: {
          boxShadow: shadows[2],
        },
        elevation4: {
          boxShadow: shadows[4],
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: shape.borderRadius.medium,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            backgroundColor: surfaceColor,
            '& fieldset': {
              borderColor: inputBorderColor,
              borderWidth: shape.borderWidth.thin,
            },
            '&:hover': {
              '& fieldset': {
                borderColor: colors.primary.main,
              },
            },
            '&.Mui-focused': {
              '& fieldset': {
                borderColor: colors.accent.main,
                borderWidth: shape.borderWidth.medium,
              },
            },
            '&.Mui-error': {
              '& fieldset': {
                borderColor: colors.error.main,
              },
            },
          },
          '& .MuiInputLabel-root': {
            color: textSecondaryColor,
            '&.Mui-focused': {
              color: colors.accent.main,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadius.small,
          fontWeight: typography.fontWeight.medium,
          fontSize: '0.7rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: shape.borderRadius.large,
          boxShadow: shadows[8],
          backgroundColor: surfaceColor,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: shadows[1],
          backgroundColor: isDark ? colors.background.dark : colors.background.paper,
          color: textPrimaryColor,
          borderRadius: 0, // Sin esquinas redondeadas
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${dividerColor}`,
          backgroundColor: isDark ? colors.background.dark : undefined,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: dividerColor,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.accent.main,
          textDecoration: 'none',
          fontWeight: typography.fontWeight.medium,
          '&:hover': {
            color: colors.accent.dark,
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: typography.fontWeight.medium,
          fontSize: '0.75rem',
          minHeight: 38.4,
          '&.Mui-selected': {
            color: colors.accent.main,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: colors.accent.main,
          height: 3,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'rgba(11, 61, 145, 0.08)',
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadius.medium,
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            borderRadius: shape.borderRadius.medium,
          },
        },
      },
    },
  };
};

const createThemeOptions = (mode: PaletteMode): ThemeOptions => {
  const palette =
    mode === 'light'
      ? {
          mode: 'light' as PaletteMode,
          primary: {
            main: colors.primary.main,
            light: colors.primary.light,
            dark: colors.primary.dark,
            contrastText: colors.primary.contrastText,
          },
          secondary: {
            main: colors.accent.main,
            light: colors.accent.light,
            dark: colors.accent.dark,
            contrastText: colors.accent.contrastText,
          },
          success: colors.success,
          warning: colors.warning,
          error: colors.error,
          info: colors.info,
          background: {
            default: colors.background.default,
            paper: colors.background.paper,
          },
          text: {
            primary: colors.text.primary,
            secondary: colors.text.secondary,
            disabled: colors.text.disabled,
          },
          divider: colors.neutral.main,
        }
      : {
          mode: 'dark' as PaletteMode,
          primary: {
            main: colors.primary.light,
            light: '#4A7BC8',
            dark: colors.primary.main,
            contrastText: '#FFFFFF',
          },
          secondary: {
            main: colors.accent.main,
            light: colors.accent.light,
            dark: colors.accent.dark,
            contrastText: colors.accent.contrastText,
          },
          success: colors.success,
          warning: colors.warning,
          error: colors.error,
          info: colors.info,
          background: {
            default: colors.background.dark,
            paper: '#1E293B',
          },
          text: {
            primary: '#F8FAFC',
            secondary: '#CBD5E1',
            disabled: '#64748B',
          },
          divider: 'rgba(255, 255, 255, 0.12)',
        };

  return {
    palette,
    typography: typographyConfig,
    shape: {
      borderRadius: shape.borderRadius.medium,
    },
    spacing: 6.4,
    shadows: shadows as any,
    components: createComponents(mode),
    custom: {
      colors: {
        accent: colors.accent,
        accentSecondary: colors.accentSecondary,
        neutral: colors.neutral,
        success: colors.success,
        badge: colors.badge,
      },
      gradients,
      logoPaths,
      shape,
      typography,
      mode,
    },
  };
};

export const theme = createTheme(createThemeOptions('light'));
export const darkTheme = createTheme(createThemeOptions('dark'));

// Exportar tipos
export type AppTheme = typeof theme;

